import { lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import ModuleDisabled from './components/errors/ModuleDisabled';
import { isModuleEnabled } from './defaults/featureFlags';
import { Loadable } from './functions/loading';

// Lazy loaded pages
export const LayoutComponent = Loadable(
  lazy(() => import('./components/nav/Layout')),
  true,
  true
);
export const LoginLayoutComponent = Loadable(
  lazy(() => import('./pages/Auth/Layout')),
  true,
  true
);

export const Home = Loadable(lazy(() => import('./pages/Index/Home')));

export const CompanyDetail = Loadable(
  lazy(() => import('./pages/company/CompanyDetail'))
);

export const CustomerDetail = Loadable(
  lazy(() => import('./pages/company/CustomerDetail'))
);

export const SupplierDetail = Loadable(
  lazy(() => import('./pages/company/SupplierDetail'))
);

export const ManufacturerDetail = Loadable(
  lazy(() => import('./pages/company/ManufacturerDetail'))
);

export const SupplierPartDetail = Loadable(
  lazy(() => import('./pages/company/SupplierPartDetail'))
);

export const ManufacturerPartDetail = Loadable(
  lazy(() => import('./pages/company/ManufacturerPartDetail'))
);

export const LocationDetail = Loadable(
  lazy(() => import('./pages/stock/LocationDetail'))
);

export const StockDetail = Loadable(
  lazy(() => import('./pages/stock/StockDetail'))
);

export const BuildIndex = Loadable(
  lazy(() => import('./pages/build/BuildIndex'))
);

export const BuildDetail = Loadable(
  lazy(() => import('./pages/build/BuildDetail'))
);

export const PurchasingIndex = Loadable(
  lazy(() => import('./pages/purchasing/PurchasingIndex'))
);

export const PurchaseOrderDetail = Loadable(
  lazy(() => import('./pages/purchasing/PurchaseOrderDetail'))
);

export const SalesIndex = Loadable(
  lazy(() => import('./pages/sales/SalesIndex'))
);

export const SalesOrderDetail = Loadable(
  lazy(() => import('./pages/sales/SalesOrderDetail'))
);

export const SalesOrderShipmentDetail = Loadable(
  lazy(() => import('./pages/sales/SalesOrderShipmentDetail'))
);

export const ReturnOrderDetail = Loadable(
  lazy(() => import('./pages/sales/ReturnOrderDetail'))
);

export const EventsIndex = Loadable(
  lazy(() => import('./pages/events/EventsIndex'))
);

export const EventDetail = Loadable(
  lazy(() => import('./pages/events/EventDetail'))
);

export const RentalsIndex = Loadable(
  lazy(() => import('./pages/rentals/RentalsIndex'))
);

export const RentalOrderDetail = Loadable(
  lazy(() => import('./pages/rentals/RentalOrderDetail'))
);

export const Scan = Loadable(lazy(() => import('./pages/Index/Scan')));

export const ErrorPage = Loadable(lazy(() => import('./pages/ErrorPage')));

export const Notifications = Loadable(
  lazy(() => import('./pages/Notifications'))
);

export const UserSettings = Loadable(
  lazy(() => import('./pages/Index/Settings/UserSettings'))
);

export const SystemSettings = Loadable(
  lazy(() => import('./pages/Index/Settings/SystemSettings'))
);

export const AdminCenter = Loadable(
  lazy(() => import('./pages/Index/Settings/AdminCenter/Index'))
);

// Core object
export const CoreIndex = Loadable(lazy(() => import('./pages/core/CoreIndex')));
export const UserDetail = Loadable(
  lazy(() => import('./pages/core/UserDetail'))
);
export const GroupDetail = Loadable(
  lazy(() => import('./pages/core/GroupDetail'))
);

export const NotFound = Loadable(
  lazy(() => import('./components/errors/NotFound'))
);

// Auth
export const Login = Loadable(lazy(() => import('./pages/Auth/Login')));
export const LoggedIn = Loadable(
  lazy(() => import('./pages/Auth/LoggedIn')),
  true,
  true
);
export const Logout = Loadable(lazy(() => import('./pages/Auth/Logout')));
export const Register = Loadable(lazy(() => import('./pages/Auth/Register')));
export const Mfa = Loadable(lazy(() => import('./pages/Auth/MFA')));
export const MfaSetup = Loadable(lazy(() => import('./pages/Auth/MFASetup')));
export const ChangePassword = Loadable(
  lazy(() => import('./pages/Auth/ChangePassword'))
);
export const Reset = Loadable(lazy(() => import('./pages/Auth/Reset')));
export const ResetPassword = Loadable(
  lazy(() => import('./pages/Auth/ResetPassword'))
);
export const VerifyEmail = Loadable(
  lazy(() => import('./pages/Auth/VerifyEmail')),
  true,
  true
);

// Routes
export const routes = (
  <Routes>
    <Route path='*' element={<NotFound />} errorElement={<ErrorPage />} />
    <Route path='/' element={<LayoutComponent />} errorElement={<ErrorPage />}>
      <Route index element={<Home />} />,
      <Route path='home/' element={<Home />} />,
      <Route path='notifications/*' element={<Notifications />} />,
      <Route path='scan/' element={<Scan />} />,
      <Route path='settings/'>
        <Route index element={<Navigate to='admin/' />} />
        <Route path='admin/*' element={<AdminCenter />} />
        <Route path='system/*' element={<SystemSettings />} />
        <Route path='user/*' element={<UserSettings />} />
      </Route>
      <Route path='part/*' element={<Navigate to='/stock/' replace />} />
      <Route path='stock/'>
        <Route index element={<Navigate to='location/index/' />} />
        <Route path='location/:id?/*' element={<LocationDetail />} />
        <Route path='item/:id/*' element={<StockDetail />} />
      </Route>
      <Route path='manufacturing/'>
        <Route
          index
          element={
            isModuleEnabled('manufacturing') ? (
              <Navigate to='index/' />
            ) : (
              <ModuleDisabled moduleName='Manufacturing' />
            )
          }
        />
        <Route
          path='index/*'
          element={
            isModuleEnabled('manufacturing') ? (
              <BuildIndex />
            ) : (
              <ModuleDisabled moduleName='Manufacturing' />
            )
          }
        />
        <Route
          path='build-order/:id/*'
          element={
            isModuleEnabled('manufacturing') ? (
              <BuildDetail />
            ) : (
              <ModuleDisabled moduleName='Manufacturing' />
            )
          }
        />
      </Route>
      <Route path='purchasing/'>
        <Route
          index
          element={
            isModuleEnabled('purchasing') ? (
              <Navigate to='index/' />
            ) : (
              <ModuleDisabled moduleName='Purchasing' />
            )
          }
        />
        <Route
          path='index/*'
          element={
            isModuleEnabled('purchasing') ? (
              <PurchasingIndex />
            ) : (
              <ModuleDisabled moduleName='Purchasing' />
            )
          }
        />
        <Route
          path='purchase-order/:id/*'
          element={
            isModuleEnabled('purchasing') ? (
              <PurchaseOrderDetail />
            ) : (
              <ModuleDisabled moduleName='Purchasing' />
            )
          }
        />
        <Route
          path='supplier/:id/*'
          element={
            isModuleEnabled('purchasing') ? (
              <SupplierDetail />
            ) : (
              <ModuleDisabled moduleName='Purchasing' />
            )
          }
        />
        <Route
          path='supplier-part/:id/*'
          element={
            isModuleEnabled('purchasing') ? (
              <SupplierPartDetail />
            ) : (
              <ModuleDisabled moduleName='Purchasing' />
            )
          }
        />
        <Route
          path='manufacturer/:id/*'
          element={
            isModuleEnabled('purchasing') ? (
              <ManufacturerDetail />
            ) : (
              <ModuleDisabled moduleName='Purchasing' />
            )
          }
        />
        <Route
          path='manufacturer-part/:id/*'
          element={
            isModuleEnabled('purchasing') ? (
              <ManufacturerPartDetail />
            ) : (
              <ModuleDisabled moduleName='Purchasing' />
            )
          }
        />
      </Route>
      <Route path='company/:id/*' element={<CompanyDetail />} />
      <Route path='sales/'>
        <Route
          index
          element={
            isModuleEnabled('sales') ? (
              <Navigate to='index/' />
            ) : (
              <ModuleDisabled moduleName='Sales' />
            )
          }
        />
        <Route
          path='index/*'
          element={
            isModuleEnabled('sales') ? (
              <SalesIndex />
            ) : (
              <ModuleDisabled moduleName='Sales' />
            )
          }
        />
        <Route
          path='sales-order/:id/*'
          element={
            isModuleEnabled('sales') ? (
              <SalesOrderDetail />
            ) : (
              <ModuleDisabled moduleName='Sales' />
            )
          }
        />
        <Route
          path='shipment/:id/*'
          element={
            isModuleEnabled('sales') ? (
              <SalesOrderShipmentDetail />
            ) : (
              <ModuleDisabled moduleName='Sales' />
            )
          }
        />
        <Route
          path='return-order/:id/*'
          element={
            isModuleEnabled('sales') ? (
              <ReturnOrderDetail />
            ) : (
              <ModuleDisabled moduleName='Sales' />
            )
          }
        />
        <Route
          path='customer/:id/*'
          element={
            isModuleEnabled('sales') ? (
              <CustomerDetail />
            ) : (
              <ModuleDisabled moduleName='Sales' />
            )
          }
        />
      </Route>
      <Route path='events/'>
        <Route index element={<Navigate to='index/' />} />
        <Route path='index/*' element={<EventsIndex />} />
        <Route path='event/:id/*' element={<EventDetail />} />
      </Route>
      <Route path='rentals/'>
        <Route index element={<Navigate to='index/' />} />
        <Route path='index/*' element={<RentalsIndex />} />
        <Route path='rental-order/:id/*' element={<RentalOrderDetail />} />
      </Route>
      <Route path='core/'>
        <Route index element={<Navigate to='index/' />} />
        <Route path='index/*' element={<CoreIndex />} />
        <Route path='user/:id/*' element={<UserDetail />} />
        <Route path='group/:id/*' element={<GroupDetail />} />
      </Route>
    </Route>
    <Route
      path='/'
      element={<LoginLayoutComponent />}
      errorElement={<ErrorPage />}
    >
      <Route path='/login' element={<Login />} />,
      <Route path='/logged-in' element={<LoggedIn />} />
      <Route path='/logout' element={<Logout />} />,
      <Route path='/register' element={<Register />} />,
      <Route path='/mfa' element={<Mfa />} />,
      <Route path='/mfa-setup' element={<MfaSetup />} />,
      <Route path='/change-password' element={<ChangePassword />} />
      <Route path='/reset-password' element={<Reset />} />
      <Route path='/set-password' element={<ResetPassword />} />
      <Route path='/verify-email/:key' element={<VerifyEmail />} />
    </Route>
  </Routes>
);
